/**
 * Old Gold Recovery — orchestrator page.
 *
 * All state, API calls and handlers live here. Presentational sub-components
 * live in ./oldgold/ and receive props only.
 *
 * Break dialog math (unchanged):
 *   Net Gold  = Current Net + Worker Gold
 *   Weight Lost = Gross − Stones − Net Gold
 *   Fine Gold (total) = Net Gold × purity
 *   Fine Gold (shop)  = Current Net × purity
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Coins, Package, Hammer, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { num } from './oldgold/constants';
import { errMsg } from './oldgold/errors';
import { uploadPhotoFile } from './oldgold/uploadPhoto';
import ReceiveTab from './oldgold/ReceiveTab';
import LotsTab from './oldgold/LotsTab';
import BreakTab from './oldgold/BreakTab';
import CreateLotDialog from './oldgold/CreateLotDialog';
import LotDetailDialog from './oldgold/LotDetailDialog';
import BreakDialog from './oldgold/BreakDialog';
import StoneEntryDialog from './oldgold/StoneEntryDialog';
import ExpectedStoneDialog from './oldgold/ExpectedStoneDialog';
import DeleteConfirmDialog from './oldgold/DeleteConfirmDialog';
import RequestOverrideDialog from './oldgold/RequestOverrideDialog';

const OldGold = () => {
  const { currentUser } = useAuth();
  const role = currentUser?.role;

  const canReceive   = ['admin', 'owner', 'inventory_dept'].includes(role);
  const canCreateLot = canReceive;
  const canBreak     = ['admin', 'owner', 'metal_inventory_assistant'].includes(role);
  const isAdmin      = ['admin', 'owner'].includes(role);

  const defaultTab = canReceive ? 'receive' : canBreak ? 'break' : 'lots';
  const [tab, setTab] = useState(defaultTab);

  // ---------- Data ----------
  const [pieces, setPieces] = useState([]);
  const [breakablePieces, setBreakablePieces] = useState([]);
  const [lots, setLots] = useState([]);

  // ---------- Receive form ----------
  const [pieceStatusFilter, setPieceStatusFilter] = useState('all');
  const [pieceSearch, setPieceSearch] = useState('');
  const [receiveForm, setReceiveForm] = useState({
    branch: '', gross_weight: '', expected_purity: '',
    expected_stone_entries: [],
    photo_url: '',
    remarks: '',
  });
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  // Expected-stone entry sub-dialog (Receive tab)
  const [expEntryOpen, setExpEntryOpen] = useState(false);
  const [expEntryForm, setExpEntryForm] = useState({ category: 'diamond', pcs: '', wt: '' });
  const [expEntryEditIndex, setExpEntryEditIndex] = useState(-1);

  // ---------- Lots ----------
  const [createLotOpen, setCreateLotOpen] = useState(false);
  const [selectedPieceIds, setSelectedPieceIds] = useState([]);
  const [lotForm, setLotForm] = useState({ remarks: '' });
  const [lotDetailOpen, setLotDetailOpen] = useState(false);
  const [lotDetail, setLotDetail] = useState(null);
  const [inStockForLot, setInStockForLot] = useState([]);

  // ---------- Break dialog ----------
  const [breakPiece, setBreakPiece] = useState(null);
  const [breakOpen, setBreakOpen] = useState(false);
  const [breakForm, setBreakForm] = useState(null);

  // Sub-dialog: add / edit a single stone entry
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryForm, setEntryForm] = useState({ category: 'diamond', pcs: '', wt: '', usability: 'usable' });
  const [entryEditIndex, setEntryEditIndex] = useState(-1);

  // ---------- Delete (admin only) ----------
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);

  // ---------- Shortage Override (MIA) ----------
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideShortage, setOverrideShortage] = useState(null); // { diamond, stone, beads_pearl }
  const [overrideBusy, setOverrideBusy] = useState(false);

  // ---------- Admin: Approvals count for the bell badge ----------
  const [approvalsCount, setApprovalsCount] = useState(0);
  const loadApprovalsCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get('/old-gold/approvals/count');
      setApprovalsCount(r.data?.count || 0);
    } catch { /* silent */ }
  }, [isAdmin]);
  useEffect(() => { loadApprovalsCount(); }, [loadApprovalsCount]);

  // ============ Loaders ============
  const loadPieces = useCallback(async () => {
    try {
      const q = {};
      if (pieceStatusFilter !== 'all') q.status = pieceStatusFilter;
      if (pieceSearch.trim()) q.search = pieceSearch.trim();
      const r = await api.get('/old-gold/pieces', { params: q });
      setPieces(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load pieces'));
    }
  }, [pieceStatusFilter, pieceSearch]);

  const loadBreakable = useCallback(async () => {
    if (!canBreak) return;
    try {
      const r = await api.get('/old-gold/pieces', { params: { status: 'issued' } });
      setBreakablePieces(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load breakable pieces'));
    }
  }, [canBreak]);

  const loadLots = useCallback(async () => {
    try {
      const r = await api.get('/old-gold/lots');
      setLots(r.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load lots'));
    }
  }, []);

  useEffect(() => { loadPieces(); }, [loadPieces]);
  useEffect(() => { loadLots(); }, [loadLots]);
  useEffect(() => { loadBreakable(); }, [loadBreakable]);
  useEffect(() => { if (tab === 'break') loadBreakable(); }, [tab, loadBreakable]);

  // ============ Receive ============
  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoUploading(true);
    try {
      const url = await uploadPhotoFile(file);
      setReceiveForm((prev) => ({ ...prev, photo_url: url }));
      toast.success('Photo attached');
    } catch (err) {
      // The helper already embeds file metadata in the message so we can
      // debug device-specific failures from the screenshot the user sends.
      toast.error(err?.message || 'Photo upload failed', { duration: 10000 });
    } finally {
      setPhotoUploading(false);
    }
  };

  // Generic photo uploader — returns URL, does NOT touch any form state.
  const uploadPhotoRaw = async (file) => {
    if (!file) return null;
    try {
      return await uploadPhotoFile(file);
    } catch (err) {
      toast.error(err?.message || 'Photo upload failed', { duration: 10000 });
      return null;
    }
  };

  const handleReceive = async (e) => {
    e.preventDefault();
    if (!receiveForm.branch.trim()) return toast.error('Branch is required');
    if (!(+receiveForm.gross_weight > 0)) return toast.error('Gross weight must be > 0');
    const p = +receiveForm.expected_purity;
    if (!(p > 0 && p <= 100)) return toast.error('Purity must be between 0 and 100');
    setReceiveLoading(true);
    try {
      const r = await api.post('/old-gold/pieces', {
        branch: receiveForm.branch.trim(),
        gross_weight: +receiveForm.gross_weight,
        expected_purity: +receiveForm.expected_purity,
        expected_stone_entries: (receiveForm.expected_stone_entries || []).map(e => ({
          category: e.category, pcs: Math.max(0, parseInt(e.pcs, 10) || 0), wt: num(e.wt),
        })),
        photo_url: receiveForm.photo_url || undefined,
        remarks: receiveForm.remarks.trim(),
      });
      toast.success(`Received ${r.data.serial_no}`);
      setReceiveForm({
        branch: receiveForm.branch,
        gross_weight: '', expected_purity: '',
        expected_stone_entries: [],
        photo_url: '', remarks: '',
      });
      loadPieces();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to save'));
    } finally {
      setReceiveLoading(false);
    }
  };

  // ---- Expected stone entry handlers (Receive form) ----
  const openAddExpected = () => {
    setExpEntryForm({ category: 'diamond', pcs: '', wt: '' });
    setExpEntryEditIndex(-1);
    setExpEntryOpen(true);
  };
  const openEditExpected = (idx) => {
    const e = receiveForm.expected_stone_entries[idx];
    setExpEntryForm({ category: e.category, pcs: String(e.pcs ?? ''), wt: String(e.wt ?? '') });
    setExpEntryEditIndex(idx);
    setExpEntryOpen(true);
  };
  const saveExpectedEntry = () => {
    const pcs = Math.max(0, parseInt(expEntryForm.pcs, 10) || 0);
    const wt = num(expEntryForm.wt);
    const weightOnly = expEntryForm.category === 'dust' || expEntryForm.category === 'others';
    if (wt <= 0) return toast.error('Enter a weight (weight is mandatory)');
    if (!weightOnly && pcs <= 0) return toast.error('Enter piece count');
    const newEntry = {
      category: expEntryForm.category,
      pcs: weightOnly ? 0 : pcs,
      wt,
    };
    setReceiveForm((prev) => {
      const list = [...(prev.expected_stone_entries || [])];
      if (expEntryEditIndex >= 0) list[expEntryEditIndex] = newEntry;
      else list.push(newEntry);
      return { ...prev, expected_stone_entries: list };
    });
    setExpEntryOpen(false);
  };
  const deleteExpectedEntry = (idx) => {
    setReceiveForm((prev) => ({
      ...prev,
      expected_stone_entries: (prev.expected_stone_entries || []).filter((_, i) => i !== idx),
    }));
  };

  // ============ Create Lot ============
  const openCreateLot = async () => {
    setSelectedPieceIds([]);
    setLotForm({ remarks: '' });
    try {
      const r = await api.get('/old-gold/pieces', { params: { status: 'in_stock' } });
      setInStockForLot(r.data);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load in-stock pieces'));
      return;
    }
    setCreateLotOpen(true);
  };
  const togglePiece = (serial) => {
    setSelectedPieceIds((prev) =>
      prev.includes(serial) ? prev.filter((x) => x !== serial) : [...prev, serial]
    );
  };
  const submitLot = async () => {
    if (!selectedPieceIds.length) return toast.error('Select at least one piece');
    try {
      const r = await api.post('/old-gold/lots', {
        piece_ids: selectedPieceIds,
        remarks: lotForm.remarks,
      });
      toast.success(`Lot ${r.data.lot_no} created`);
      setCreateLotOpen(false);
      setSelectedPieceIds([]);
      loadPieces(); loadLots(); loadBreakable();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to create lot'));
    }
  };

  const openLotDetail = async (lotId) => {
    try {
      const r = await api.get(`/old-gold/lots/${lotId}`);
      setLotDetail(r.data);
      setLotDetailOpen(true);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load lot'));
    }
  };

  // ============ Break ============
  const emptyBreakForm = () => ({
    current_net_weight: '', worker_gold_wt: '',
    stone_entries: [],
    remarks: '',
  });

  const openBreak = async (piece) => {
    try {
      const r = await api.get(`/old-gold/pieces/${piece.serial_no || piece._id}`);
      const p = r.data;
      setBreakPiece(p);
      if (p.breakage_draft) {
        const d = p.breakage_draft;
        setBreakForm({
          current_net_weight: d.current_net_weight ?? '',
          worker_gold_wt: d.worker_gold_wt ?? '',
          stone_entries: Array.isArray(d.stone_entries) ? d.stone_entries.map(e => ({ ...e })) : [],
          remarks: d.remarks ?? '',
        });
      } else {
        setBreakForm(emptyBreakForm());
      }
      setBreakOpen(true);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to open piece'));
    }
  };

  const buildBreakPayload = () => ({
    current_net_weight: num(breakForm.current_net_weight),
    worker_gold_wt: num(breakForm.worker_gold_wt),
    stone_entries: (breakForm.stone_entries || []).map(e => ({
      category: e.category,
      pcs: Math.max(0, parseInt(e.pcs, 10) || 0),
      wt: num(e.wt),
      usability: e.usability || (e.usable ? 'usable' : 'not_usable'),
    })),
    remarks: breakForm.remarks || '',
  });

  const saveDraft = async () => {
    try {
      await api.post(`/old-gold/pieces/${breakPiece.serial_no}/break/draft`, buildBreakPayload());
      toast.success('Draft saved');
      setBreakOpen(false);
      loadPieces(); loadBreakable();
    } catch (err) {
      const status = err.response?.status;
      toast.error(errMsg(err, 'Failed to save draft'), { duration: status === 409 ? 8000 : 4000 });
    }
  };

  const confirmBreak = async () => {
    const cn = num(breakForm.current_net_weight);
    if (cn <= 0) return toast.error('Enter Current Net Weight');

    if (!window.confirm('Finalize breakage for this piece? This cannot be edited later.')) return;
    try {
      await api.post(`/old-gold/pieces/${breakPiece.serial_no}/break/confirm`, buildBreakPayload());
      toast.success(`${breakPiece.serial_no} finalized`);
      setBreakOpen(false);
      loadPieces(); loadLots(); loadBreakable();
      if (lotDetail && breakPiece.lot_id === lotDetail._id) openLotDetail(lotDetail._id);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      // Shortage detected — open Request Override dialog instead of a plain toast
      if (status === 409 && detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code === 'shortage_requires_approval') {
        setOverrideShortage(detail.shortage || {});
        setOverrideOpen(true);
        return;
      }
      toast.error(errMsg(err, 'Failed to confirm'), { duration: status === 409 ? 8000 : 4000 });
    }
  };

  const submitOverrideRequest = async ({ reason, note, photo_url }) => {
    if (!breakPiece || !breakForm) return;
    setOverrideBusy(true);
    try {
      await api.post(`/old-gold/pieces/${breakPiece.serial_no}/break/request-override`, {
        reason, note, photo_url,
        breakage: buildBreakPayload(),
      });
      toast.success('Sent for admin approval', { duration: 5000 });
      setOverrideOpen(false);
      setBreakOpen(false);
      loadPieces(); loadLots(); loadBreakable(); loadApprovalsCount();
    } catch (err) {
      toast.error(errMsg(err, 'Failed to send request'));
    } finally {
      setOverrideBusy(false);
    }
  };

  // ---- stone entry sub-dialog ----
  const openAddEntry = () => {
    // Plain-gold pieces default to 'dust' since inserts are locked.
    const exp = (breakPiece && breakPiece.expected_stones) || {};
    const hasAnyInsert = ['diamond', 'stone', 'beads_pearl'].some(
      (c) => (parseInt(exp[c], 10) || 0) > 0
    );
    setEntryForm({
      category: hasAnyInsert ? 'diamond' : 'dust',
      pcs: '', wt: '', usability: 'usable',
    });
    setEntryEditIndex(-1);
    setEntryOpen(true);
  };
  const openEditEntry = (idx) => {
    const e = breakForm.stone_entries[idx];
    const usability = e.usability || (e.usable ? 'usable' : 'not_usable');
    setEntryForm({
      category: e.category,
      pcs: String(e.pcs ?? ''),
      wt: String(e.wt ?? ''),
      usability,
    });
    setEntryEditIndex(idx);
    setEntryOpen(true);
  };
  const saveEntry = () => {
    const wt = num(entryForm.wt);
    const weightOnly = entryForm.category === 'dust' || entryForm.category === 'others';
    if (wt <= 0) return toast.error('Enter a weight');
    let pcs = 0;
    if (!weightOnly) {
      pcs = Math.max(0, parseInt(entryForm.pcs, 10) || 0);
      if (pcs <= 0) return toast.error('Enter piece count');
    }
    const newEntry = weightOnly
      ? { category: entryForm.category, pcs: 0, wt, usability: 'usable' }
      : { category: entryForm.category, pcs, wt, usability: entryForm.usability };
    setBreakForm((prev) => {
      const list = [...prev.stone_entries];
      if (entryEditIndex >= 0) list[entryEditIndex] = newEntry;
      else list.push(newEntry);
      return { ...prev, stone_entries: list };
    });
    setEntryOpen(false);
  };
  const deleteEntry = (idx) => {
    setBreakForm((prev) => ({
      ...prev,
      stone_entries: prev.stone_entries.filter((_, i) => i !== idx),
    }));
  };

  // ---- live computed for break dialog ----
  const liveCalc = useMemo(() => {
    if (!breakForm || !breakPiece) return null;
    const gross = num(breakPiece.gross_weight);
    const cn = num(breakForm.current_net_weight);
    const wg = num(breakForm.worker_gold_wt);
    const stoneWt = (breakForm.stone_entries || []).reduce((s, e) => s + num(e.wt), 0);
    const netGold = cn + wg;
    const weightLost = Math.max(0, gross - stoneWt - netGold);
    const purityFrac = num(breakPiece.expected_purity) / 100;
    const fineTotal = netGold * purityFrac;
    const fineShop = cn * purityFrac;
    return { gross, cn, wg, stoneWt, netGold, weightLost, fineTotal, fineShop };
  }, [breakForm, breakPiece]);

  // ---------- Delete handlers ----------
  const openDeletePiece = (piece) => {
    setDeleteTarget({ kind: 'piece', id: piece.serial_no, label: piece.serial_no });
    setDeleteConfirmText('');
  };
  const openDeleteLot = (lot) => {
    setDeleteTarget({ kind: 'lot', id: lot._id, label: lot.lot_no });
    setDeleteConfirmText('');
  };
  const cancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };
  const submitDelete = async () => {
    if (!deleteTarget || deleteConfirmText.trim() !== deleteTarget.label) return;
    setDeleteBusy(true);
    try {
      if (deleteTarget.kind === 'piece') {
        await api.delete(`/old-gold/pieces/${deleteTarget.id}`);
        setPieces((prev) => prev.filter((p) => p.serial_no !== deleteTarget.id));
        toast.success(`${deleteTarget.label} deleted`);
        loadPieces();
      } else {
        await api.delete(`/old-gold/lots/${deleteTarget.id}`);
        setLots((prev) => prev.filter((l) => l._id !== deleteTarget.id));
        toast.success(`Lot ${deleteTarget.label} deleted · pieces reverted to stock`);
        setLotDetailOpen(false);
        loadLots(); loadPieces(); loadBreakable();
      }
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      toast.error(errMsg(err, 'Delete failed'));
    } finally {
      setDeleteBusy(false);
    }
  };

  // ============================================================
  //                           RENDER
  // ============================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50/40 pb-20">
      <header className="bg-gradient-to-r from-amber-800 to-yellow-700 text-white shadow sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 sm:w-6 sm:h-6" />
            <h1 className="text-base sm:text-xl font-semibold" data-testid="old-gold-title">
              Old Gold Recovery
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Link
                to="/old-gold/approvals"
                className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs"
                data-testid="approvals-nav-btn"
                title="Shortage approvals"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Approvals</span>
                {approvalsCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-[10px] font-bold grid place-items-center"
                    data-testid="approvals-badge-count"
                  >
                    {approvalsCount}
                  </span>
                )}
              </Link>
            )}
            <GlobalNavigationMenu />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        <Tabs value={tab} onValueChange={setTab} data-testid="old-gold-tabs">
          <TabsList className="w-full grid grid-cols-3">
            {canReceive && (
              <TabsTrigger value="receive" data-testid="tab-receive" className="text-xs sm:text-sm">
                <Package className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Receive</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="lots" data-testid="tab-lots" className="text-xs sm:text-sm">
              <Coins className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Lots</span>
            </TabsTrigger>
            {canBreak && (
              <TabsTrigger value="break" data-testid="tab-break" className="text-xs sm:text-sm">
                <Hammer className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Breakage</span>
              </TabsTrigger>
            )}
          </TabsList>

          {canReceive && (
            <ReceiveTab
              receiveForm={receiveForm} setReceiveForm={setReceiveForm}
              receiveLoading={receiveLoading} photoUploading={photoUploading}
              onSubmit={handleReceive} onUploadPhoto={uploadPhoto}
              onOpenAddExpected={openAddExpected}
              onOpenEditExpected={openEditExpected}
              onDeleteExpected={deleteExpectedEntry}
              pieces={pieces} pieceSearch={pieceSearch} setPieceSearch={setPieceSearch}
              pieceStatusFilter={pieceStatusFilter} setPieceStatusFilter={setPieceStatusFilter}
              onRefreshPieces={loadPieces}
              isAdmin={isAdmin} onDeletePiece={openDeletePiece}
            />
          )}

          <LotsTab
            lots={lots}
            onRefresh={loadLots}
            canCreateLot={canCreateLot}
            onCreateLot={openCreateLot}
            onOpenLot={openLotDetail}
          />

          {canBreak && (
            <BreakTab
              role={role}
              breakablePieces={breakablePieces}
              onRefresh={loadBreakable}
              onOpenBreak={openBreak}
            />
          )}
        </Tabs>
      </main>

      <CreateLotDialog
        open={createLotOpen} onOpenChange={setCreateLotOpen}
        lotForm={lotForm} setLotForm={setLotForm}
        inStockForLot={inStockForLot}
        selectedPieceIds={selectedPieceIds} togglePiece={togglePiece}
        onSubmit={submitLot}
      />

      <LotDetailDialog
        open={lotDetailOpen} onOpenChange={setLotDetailOpen}
        lotDetail={lotDetail}
        isAdmin={isAdmin}
        onDelete={openDeleteLot}
      />

      <BreakDialog
        open={breakOpen} onOpenChange={setBreakOpen}
        breakPiece={breakPiece} breakForm={breakForm} setBreakForm={setBreakForm}
        liveCalc={liveCalc}
        onOpenAddEntry={openAddEntry}
        onOpenEditEntry={openEditEntry}
        onDeleteEntry={deleteEntry}
        onSaveDraft={saveDraft}
        onConfirm={confirmBreak}
      />

      <StoneEntryDialog
        open={entryOpen} onOpenChange={setEntryOpen}
        form={entryForm} setForm={setEntryForm}
        editIndex={entryEditIndex}
        onSave={saveEntry}
        disabledInsertCats={(() => {
          // Plain-gold rule (loose): if receipt declared NO insert (diamond/stone/beads_pearl)
          // then disable those three category buttons — MIA can only add Dust/Others.
          if (!breakPiece) return [];
          const exp = breakPiece.expected_stones || {};
          const hasAnyInsert = ['diamond', 'stone', 'beads_pearl'].some(
            (c) => (parseInt(exp[c], 10) || 0) > 0
          );
          return hasAnyInsert ? [] : ['diamond', 'stone', 'beads_pearl'];
        })()}
      />

      <ExpectedStoneDialog
        open={expEntryOpen} onOpenChange={setExpEntryOpen}
        form={expEntryForm} setForm={setExpEntryForm}
        editIndex={expEntryEditIndex}
        onSave={saveExpectedEntry}
      />

      <DeleteConfirmDialog
        target={deleteTarget}
        confirmText={deleteConfirmText}
        setConfirmText={setDeleteConfirmText}
        busy={deleteBusy}
        onCancel={cancelDelete}
        onConfirm={submitDelete}
      />

      <RequestOverrideDialog
        open={overrideOpen}
        onOpenChange={setOverrideOpen}
        serial={breakPiece?.serial_no}
        shortage={overrideShortage}
        onSubmit={submitOverrideRequest}
        uploadPhoto={uploadPhotoRaw}
        submitting={overrideBusy}
      />
    </div>
  );
};

export default OldGold;
